import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { get, set } from "idb-keyval";
import { useReferenceValues } from "../../../hooks/useReferenceValues";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TacticalPosition {
  /** x‑percent of the field width (0 = left touchline, 100 = right touchline)
   *  Stored in canonical orientation: home team attacks left→right. */
  x: number;
  /** y‑percent of the field height (0 = top touchline, 100 = bottom touchline) */
  y: number;
}

export type PositionGroup =
  | "goalkeeper"
  | "defense"
  | "midfield"
  | "attack"
  | "other";

interface PositionBounds {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

// ---------------------------------------------------------------------------
// Formation types
// ---------------------------------------------------------------------------

/**
 * A formation is an array of numbers representing the count of outfield
 * players in each vertical line from defense → attack.
 * Example: [4, 4, 2] = 4‑4‑2, [4, 3, 3] = 4‑3‑3, [3, 5, 2] = 3‑5‑2.
 * The GK is always implicitly placed and NOT counted in the sum.
 * The sum MUST equal 10 (outfield players).
 */
export type Formation = number[];

export interface FormationPreset {
  label: string;
  formation: Formation;
}

/** Predefined formation library — sorted by popularity. */
export const FORMATION_PRESETS: FormationPreset[] = [
  { label: "4-4-2", formation: [4, 4, 2] },
  { label: "4-3-3", formation: [4, 3, 3] },
  { label: "4-2-3-1", formation: [4, 2, 3, 1] },
  { label: "4-4-1-1", formation: [4, 4, 1, 1] },
  { label: "3-5-2", formation: [3, 5, 2] },
  { label: "3-4-3", formation: [3, 4, 3] },
  { label: "4-1-4-1", formation: [4, 1, 4, 1] },
  { label: "4-3-2-1", formation: [4, 3, 2, 1] },
  { label: "4-5-1", formation: [4, 5, 1] },
  { label: "5-3-2", formation: [5, 3, 2] },
  { label: "5-4-1", formation: [5, 4, 1] },
  { label: "4-1-2-1-2", formation: [4, 1, 2, 1, 2] },
  { label: "3-4-1-2", formation: [3, 4, 1, 2] },
];

/** Validate a formation: must sum to 10 and have 2-6 lines. */
export const isValidFormation = (f: Formation): boolean =>
  f.length >= 2 &&
  f.length <= 6 &&
  f.every((n) => n >= 1 && Number.isInteger(n)) &&
  f.reduce((a, b) => a + b, 0) === 10;

/** Parse a formation string like "4-3-3" into a Formation array. */
export const parseFormationString = (s: string): Formation | null => {
  const parts = s.replace(/\s/g, "").split("-").map(Number);
  if (parts.some(isNaN)) return null;
  return isValidFormation(parts) ? parts : null;
};

// ---------------------------------------------------------------------------
// Bounds per position group
// ---------------------------------------------------------------------------

const HOME_BOUNDS: Record<PositionGroup, PositionBounds> = {
  goalkeeper: { xMin: 0, xMax: 20, yMin: 15, yMax: 85 },
  // Keep every team on its own half pre-match; away bounds are mirrored.
  defense: { xMin: 5, xMax: 40, yMin: 0, yMax: 100 },
  midfield: { xMin: 18, xMax: 49, yMin: 0, yMax: 100 },
  attack: { xMin: 30, xMax: 49, yMin: 0, yMax: 100 },
  other: { xMin: 0, xMax: 49, yMin: 0, yMax: 100 },
};

/** During a live match players can roam the entire field. */
const LIVE_HOME_BOUNDS: Record<PositionGroup, PositionBounds> = {
  goalkeeper: { xMin: 0, xMax: 100, yMin: 0, yMax: 100 },
  defense: { xMin: 0, xMax: 100, yMin: 0, yMax: 100 },
  midfield: { xMin: 0, xMax: 100, yMin: 0, yMax: 100 },
  attack: { xMin: 0, xMax: 100, yMin: 0, yMax: 100 },
  other: { xMin: 0, xMax: 100, yMin: 0, yMax: 100 },
};

/** Mirror bounds for the away team (home bounds reflected). */
const mirrorBounds = (b: PositionBounds): PositionBounds => ({
  xMin: 100 - b.xMax,
  xMax: 100 - b.xMin,
  yMin: b.yMin,
  yMax: b.yMax,
});

// ---------------------------------------------------------------------------
// Utility helpers (exported for testing)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Position-group source of truth — reference-data API, fuzzy-match fallback
// ---------------------------------------------------------------------------
//
// Every `player_position` reference value carries a `group` computed from
// real football domain knowledge (see the backend's PMA-BE-077). The fuzzy
// matcher below predates that and is known to misclassify 10 of the 19
// default codes (LWB, RWB, SW, LM, RM, LW, RW, LF, RF, SS all fall through
// to "other"). `setPositionGroupOverrides` lets `usePositionGroupSync`
// (mounted once in `CockpitContext.tsx`) push the API's exact code→group
// mapping in here; `getPositionGroup` consults it first and only falls back
// to the fuzzy match for a code the override doesn't know about (cache not
// yet warmed, or a genuinely novel/unsynced code) — same safe-degradation
// shape as the backend's own position-code cache.
let positionGroupOverrides: Record<string, PositionGroup> = {};

/** @internal exported for `usePositionGroupSync` and tests only. */
export const setPositionGroupOverrides = (
  overrides: Record<string, PositionGroup>,
) => {
  positionGroupOverrides = overrides;
};

const normalizePos = (v: string) =>
  v
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .trim();

export const getPositionGroup = (raw?: string | null): PositionGroup => {
  if (
    raw &&
    Object.prototype.hasOwnProperty.call(positionGroupOverrides, raw)
  ) {
    return positionGroupOverrides[raw];
  }
  const n = normalizePos(raw || "");
  if (!n) return "other";
  if (["gk", "goalkeeper", "portero", "keeper"].some((k) => n.includes(k)))
    return "goalkeeper";
  if (
    [
      "def",
      "back",
      "lateral",
      "carrilero",
      "libero",
      "cb",
      "lb",
      "rb",
      "df",
    ].some((k) => n.includes(k))
  )
    return "defense";
  if (
    ["mid", "centro", "medio", "volante", "cm", "dm", "am", "mf"].some((k) =>
      n.includes(k),
    )
  )
    return "midfield";
  if (
    [
      "fwd",
      "forward",
      "att",
      "striker",
      "delantero",
      "extremo",
      "wing",
      "st",
      "cf",
      "fw",
    ].some((k) => n.includes(k))
  )
    return "attack";
  return "other";
};

/**
 * Mount once (in `CockpitContext.tsx`) to keep `getPositionGroup`'s override
 * cache in sync with the reference-data API. `includeInactive: true` is
 * deliberate — a deactivated position code must keep classifying correctly
 * for players already on the pitch using it (mirrors the backend's own
 * all-codes-regardless-of-active-status cache for the identical reason).
 */
export const usePositionGroupSync = (): void => {
  const { data } = useReferenceValues("player_position", {
    includeInactive: true,
  });

  useEffect(() => {
    if (!data) return;
    const overrides: Record<string, PositionGroup> = {};
    for (const value of data) {
      if (value.group) overrides[value.code] = value.group;
    }
    setPositionGroupOverrides(overrides);
  }, [data]);
};

/** Clamp a position to stay within bounds. */
export const clampToBounds = (
  pos: TacticalPosition,
  bounds: PositionBounds,
): TacticalPosition => ({
  x: Math.min(bounds.xMax, Math.max(bounds.xMin, pos.x)),
  y: Math.min(bounds.yMax, Math.max(bounds.yMin, pos.y)),
});

export const getBoundsForPlayer = (
  positionString: string | undefined,
  side: "home" | "away",
  isMatchLive = false,
): PositionBounds => {
  const group = getPositionGroup(positionString);
  const table = isMatchLive ? LIVE_HOME_BOUNDS : HOME_BOUNDS;
  const base = table[group];
  return side === "away" ? mirrorBounds(base) : base;
};

// ---------------------------------------------------------------------------
// Collision avoidance — minimum separation between any two nodes (in %‑units)
// ---------------------------------------------------------------------------

/** Minimum distance (in field‑% units) between the centres of any two players.
 *  At a typical 600 px field width this ≈ 36 px — comfortably larger than the
 *  40 px node circle so labels don't overlap either. */
export const MIN_PLAYER_SEPARATION = 6;

/** Given a candidate position and a map of all current positions, nudge the
 *  candidate away from any node that is too close, staying within bounds.
 *  Returns the adjusted position. */
export const resolveCollision = (
  playerId: string,
  candidate: TacticalPosition,
  allPositions: Map<string, TacticalPosition>,
  bounds: PositionBounds,
  maxIterations = 10,
): TacticalPosition => {
  let pos = { ...candidate };
  for (let iter = 0; iter < maxIterations; iter++) {
    let pushed = false;
    for (const [otherId, otherPos] of allPositions) {
      if (otherId === playerId) continue;
      const dx = pos.x - otherPos.x;
      const dy = pos.y - otherPos.y;
      const dist = Math.hypot(dx, dy);
      if (dist < MIN_PLAYER_SEPARATION && dist > 0.01) {
        // Push candidate away from the other node
        const push = (MIN_PLAYER_SEPARATION - dist) / 2 + 0.5;
        const angle = Math.atan2(dy, dx);
        pos = {
          x: pos.x + Math.cos(angle) * push,
          y: pos.y + Math.sin(angle) * push,
        };
        pushed = true;
      } else if (dist <= 0.01) {
        // Exactly overlapping — push in a deterministic direction
        pos = { x: pos.x + MIN_PLAYER_SEPARATION * 0.6, y: pos.y + 0.5 };
        pushed = true;
      }
    }
    // Reclamp after each iteration
    pos = clampToBounds(pos, bounds);
    if (!pushed) break;
  }
  return pos;
};

// ---------------------------------------------------------------------------
// IndexedDB Persistence — save/load per‑match tactical positions
// ---------------------------------------------------------------------------

// v2: bumped from "tactical-positions-" to discard cached pre-grid-algorithm
// default positions for existing matches (one-time invalidation, 2026-06-08).
// Formation cache (IDB_FORMATION_PREFIX) is intentionally NOT bumped.
const IDB_KEY_PREFIX = "tactical-positions-v2-";

const serializePositions = (
  positions: Map<string, TacticalPosition>,
): string => {
  const obj: Record<string, TacticalPosition> = {};
  positions.forEach((v, k) => {
    obj[k] = v;
  });
  return JSON.stringify(obj);
};

const deserializePositions = (
  raw: string,
): Map<string, TacticalPosition> | null => {
  try {
    const obj = JSON.parse(raw) as Record<string, TacticalPosition>;
    const map = new Map<string, TacticalPosition>();
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v.x === "number" && typeof v.y === "number") {
        map.set(k, v);
      }
    }
    return map.size > 0 ? map : null;
  } catch {
    return null;
  }
};

const savePositionsToIDB = async (
  matchId: string,
  positions: Map<string, TacticalPosition>,
) => {
  try {
    await set(IDB_KEY_PREFIX + matchId, serializePositions(positions));
  } catch {
    // Silently fail — persistence is best-effort
  }
};

const loadPositionsFromIDB = async (
  matchId: string,
): Promise<Map<string, TacticalPosition> | null> => {
  try {
    const raw = await get(IDB_KEY_PREFIX + matchId);
    if (typeof raw !== "string") return null;
    return deserializePositions(raw);
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Formation IDB persistence — per‑match, per‑side
// ---------------------------------------------------------------------------

const IDB_FORMATION_PREFIX = "tactical-formation-";

interface FormationState {
  home: Formation | null;
  away: Formation | null;
}

const saveFormationsToIDB = async (matchId: string, state: FormationState) => {
  try {
    await set(IDB_FORMATION_PREFIX + matchId, JSON.stringify(state));
  } catch {
    // best-effort
  }
};

const loadFormationsFromIDB = async (
  matchId: string,
): Promise<FormationState | null> => {
  try {
    const raw = await get(IDB_FORMATION_PREFIX + matchId);
    if (typeof raw !== "string") return null;
    const parsed = JSON.parse(raw) as FormationState;
    if (
      (parsed.home === null ||
        (Array.isArray(parsed.home) && isValidFormation(parsed.home))) &&
      (parsed.away === null ||
        (Array.isArray(parsed.away) && isValidFormation(parsed.away)))
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// resolveDefaultPositions — compute initial positions for a team's players
// ---------------------------------------------------------------------------

interface PlayerLike {
  id: string;
  position: string;
}

/**
 * Given a list of players for one side, return a map of player‑id → canonical
 * TacticalPosition.
 *
 * Layout strategy — robust to all-identical position tags (e.g. every player
 * tagged "MF" in E2E data):
 *
 *  • GK(s)  → x = 6 % (home) / 94 % (away), y = 50 % (or spread if >1)
 *  • Outfield → evenly distributed into a grid across the team's own half.
 *    Column count: clamp(3, 5, round(√n) + 1).  Home x-range [14 %→46 %],
 *    away mirrors [86 %→54 %].  Within each column, rows spread y 14 %→88 %.
 *    Players are sorted defense→other→midfield→attack so the column nearest
 *    the own goal gets defenders; column nearest centre gets forwards.
 *
 * The field [4,96] field bounds are used for clamping — NOT getBoundsForPlayer
 * (pre-match per-group bounds would pull outfield players back toward centre).
 */
export const resolveDefaultPositions = (
  players: PlayerLike[],
  side: "home" | "away",
): Map<string, TacticalPosition> => {
  const result = new Map<string, TacticalPosition>();
  if (players.length === 0) return result;

  /** Clamp to playable field area — avoids tokens sitting on the pitch edge. */
  const FIELD: PositionBounds = { xMin: 4, xMax: 96, yMin: 4, yMax: 96 };

  // ── 1. Separate goalkeepers from outfield ─────────────────────────────
  const gkList = players.filter(
    (p) => getPositionGroup(p.position) === "goalkeeper",
  );
  const outfield = players.filter(
    (p) => getPositionGroup(p.position) !== "goalkeeper",
  );

  // ── 2. Place GK(s) at own-goal edge ───────────────────────────────────
  gkList.forEach((p, i) => {
    const x = side === "away" ? 94 : 6;
    const y = gkList.length === 1 ? 50 : 14 + (74 / (gkList.length - 1)) * i;
    result.set(p.id, clampToBounds({ x, y }, FIELD));
  });

  // ── 3. Grid outfield players across own half ───────────────────────────
  const n = outfield.length;
  if (n === 0) return result;

  // Sort by positional depth so column 0 (own goal side) gets defenders.
  const GROUP_DEPTH: Record<PositionGroup, number> = {
    goalkeeper: 0,
    defense: 1,
    other: 2,
    midfield: 3,
    attack: 4,
  };
  const sorted = [...outfield].sort(
    (a, b) =>
      GROUP_DEPTH[getPositionGroup(a.position)] -
      GROUP_DEPTH[getPositionGroup(b.position)],
  );

  // Column count: sqrt-based, clamped 3–5.
  // n=10→4 cols, n=3→3 cols, n=25→6→clamped to 5 cols.
  const cols = Math.max(3, Math.min(5, Math.round(Math.sqrt(n)) + 1));

  // Home x-range [14→46]; away mirrors [86→54].
  // col=0 is nearest own goal, col=cols-1 is nearest centre.
  const xNear = side === "away" ? 86 : 14;
  const xFar = side === "away" ? 54 : 46;

  let playerIdx = 0;
  for (let c = 0; c < cols && playerIdx < n; c++) {
    // Distribute remaining players as evenly as possible across remaining cols
    const playersLeft = n - playerIdx;
    const colsLeft = cols - c;
    const colSize = Math.ceil(playersLeft / colsLeft);

    const x =
      cols === 1
        ? (xNear + xFar) / 2
        : xNear + ((xFar - xNear) * c) / (cols - 1);

    for (let row = 0; row < colSize && playerIdx < n; row++) {
      const y = colSize === 1 ? 50 : 14 + (74 / (colSize - 1)) * row;
      result.set(sorted[playerIdx].id, clampToBounds({ x, y }, FIELD));
      playerIdx++;
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// resolveFormationPositions — place players using a formation template
// ---------------------------------------------------------------------------

/**
 * Given a team's on‑field players and a formation array, distribute them on
 * vertical lines across the field half.  The GK is placed at x=5 (home) and
 * every other player is spread evenly across `formation.length` lines from
 * defense to attack.
 *
 * Players are sorted by their backend position to produce a deterministic
 * mapping (GK → GK slot, defenders sorted to defense line, etc.).  When the
 * formation has more lines than can be cleanly mapped from position groups we
 * just fill back-to-front.
 */
export const resolveFormationPositions = (
  players: PlayerLike[],
  side: "home" | "away",
  formation: Formation,
): Map<string, TacticalPosition> => {
  const result = new Map<string, TacticalPosition>();
  if (!isValidFormation(formation) || players.length === 0) return result;

  // Separate GK
  const gk = players.find((p) => getPositionGroup(p.position) === "goalkeeper");
  const outfield = players.filter((p) => p !== gk);

  // Place GK
  if (gk) {
    const gkX = side === "away" ? 95 : 5;
    result.set(gk.id, { x: gkX, y: 50 });
  }

  // Sort outfield players by positional depth (defense → attack) so they
  // fill the formation lines logically.
  const groupOrder: PositionGroup[] = [
    "defense",
    "midfield",
    "attack",
    "other",
  ];
  const sortedOutfield = [...outfield].sort((a, b) => {
    const ga = groupOrder.indexOf(getPositionGroup(a.position));
    const gb = groupOrder.indexOf(getPositionGroup(b.position));
    return ga - gb;
  });

  // X positions for each formation line — evenly across the half (15–80%)
  const lineCount = formation.length;
  const xPositions = formation.map((_, idx) => {
    // lineCount lines spread from 15% to 80% for home
    return 15 + (idx / Math.max(1, lineCount - 1)) * 65;
  });

  // Assign players to lines
  let playerIdx = 0;
  for (let lineIdx = 0; lineIdx < lineCount; lineIdx++) {
    const count = formation[lineIdx];
    const lineX = xPositions[lineIdx];

    for (let j = 0; j < count && playerIdx < sortedOutfield.length; j++) {
      const player = sortedOutfield[playerIdx];
      let x = lineX;
      let y = 50;

      if (count > 1) {
        // Spread players vertically within the line
        const spread = Math.max(16, Math.min(30, 70 / count));
        const offset = (j - (count - 1) / 2) * spread;
        y = Math.min(92, Math.max(8, 50 + offset));
      }

      if (side === "away") {
        x = 100 - x;
      }
      const bounds = getBoundsForPlayer(player.position, side);
      result.set(player.id, clampToBounds({ x, y }, bounds));
      playerIdx++;
    }
  }

  // Fallback: place any remaining players (if more than 10 outfield)
  while (playerIdx < sortedOutfield.length) {
    const player = sortedOutfield[playerIdx];
    const x = side === "away" ? 60 : 40;
    result.set(player.id, { x, y: 50 });
    playerIdx++;
  }

  return result;
};

// ---------------------------------------------------------------------------
// Hook — useTacticalPositions
// ---------------------------------------------------------------------------

interface UseTacticalPositionsOptions {
  matchId: string | undefined;
  homePlayers: PlayerLike[];
  awayPlayers: PlayerLike[];
  /** When true (live match), players can be dragged across the entire field. */
  isMatchLive?: boolean;
}

export const useTacticalPositions = ({
  matchId,
  homePlayers,
  awayPlayers,
  isMatchLive = false,
}: UseTacticalPositionsOptions) => {
  // Canonical positions (home = left). Keyed by player id.
  const [positions, setPositions] = useState<Map<string, TacticalPosition>>(
    () => new Map(),
  );

  /**
   * PMA-COCK-150 (symptom 2 — "many balls go to the centre" on load/refresh):
   * `positions` starts empty and is only populated once the init effect
   * below's ASYNC work (`loadPositionsFromIDB`, genuinely async — IndexedDB)
   * resolves. Between "the roster is available and player tokens start
   * rendering" and "that effect's `setPositions` call," any player id not
   * yet in `positions` fell back to a hardcoded `{x:50,y:50}` in
   * `getDisplayPosition` below — every on-field player at once, stacked on
   * the exact centre spot. Live-reproduced: sampling `data-tactical-x/y`
   * every 50ms right after a fresh match mount caught all 22 tokens at
   * exactly (50,50) for one frame before self-correcting. Normally
   * sub-100ms and invisible locally, but the window scales with real IDB/
   * network latency — long enough to be the client's actual multi-second
   * complaint in production. This memo recomputes the same grid layout the
   * init effect will end up computing anyway, synchronously on every
   * roster-shape change, so `getDisplayPosition`'s fallback below has a
   * spread-out placeholder instead of a literal centre point to fall back
   * to while the real (possibly IDB-restored) position is still loading.
   * Once the async effect's `setPositions` resolves, `positions.get(id)`
   * hits directly and this map is never consulted for that id again.
   */
  const syncDefaultPositions = useMemo(
    () =>
      new Map([
        ...resolveDefaultPositions(homePlayers, "home"),
        ...resolveDefaultPositions(awayPlayers, "away"),
      ]),
    [homePlayers, awayPlayers],
  );

  const prevMatchId = useRef<string | undefined>(undefined);
  /** Debounce handle for persist writes. */
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Track whether we've loaded from IDB for the current match. */
  const idbLoadedFor = useRef<string | undefined>(undefined);

  // Formation state
  const [homeFormation, setHomeFormation] = useState<Formation | null>(null);
  const [awayFormation, setAwayFormation] = useState<Formation | null>(null);
  /** Track formation IDB loading. */
  const idbFormationLoadedFor = useRef<string | undefined>(undefined);
  /** Keep latest rosters in refs so applyFormation can read them synchronously. */
  const homePlayersRef = useRef(homePlayers);
  homePlayersRef.current = homePlayers;
  const awayPlayersRef = useRef(awayPlayers);
  awayPlayersRef.current = awayPlayers;

  // Persist positions to IndexedDB (debounced 500 ms)
  const persistPositions = useCallback(
    (nextPositions: Map<string, TacticalPosition>) => {
      if (!matchId) return;
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => {
        savePositionsToIDB(matchId, nextPositions);
      }, 500);
    },
    [matchId],
  );

  // (Re)initialise when match or rosters change — load from IDB first
  useEffect(() => {
    if (!matchId) return;
    // Don't initialise until we actually have players (match data has loaded)
    if (homePlayers.length === 0 && awayPlayers.length === 0) return;

    const isNewMatch = prevMatchId.current !== matchId;
    prevMatchId.current = matchId;

    let cancelled = false;

    const init = async () => {
      // Try IDB load on new match or first init
      let restored: Map<string, TacticalPosition> | null = null;
      if (isNewMatch || idbLoadedFor.current !== matchId) {
        idbLoadedFor.current = matchId;
        restored = await loadPositionsFromIDB(matchId);
      }

      // Load saved formations
      let savedFormations: FormationState | null = null;
      if (isNewMatch || idbFormationLoadedFor.current !== matchId) {
        idbFormationLoadedFor.current = matchId;
        savedFormations = await loadFormationsFromIDB(matchId);
      }

      if (cancelled) return;

      // Restore formation state
      if (savedFormations) {
        setHomeFormation(savedFormations.home);
        setAwayFormation(savedFormations.away);
      } else if (isNewMatch) {
        setHomeFormation(null);
        setAwayFormation(null);
      }

      // Build fresh defaults — use formation-based positioning when a
      // formation has been persisted for the side.
      const hf = savedFormations?.home ?? null;
      const af = savedFormations?.away ?? null;

      const homeMap =
        hf && isValidFormation(hf)
          ? resolveFormationPositions(homePlayers, "home", hf)
          : resolveDefaultPositions(homePlayers, "home");
      const awayMap =
        af && isValidFormation(af)
          ? resolveFormationPositions(awayPlayers, "away", af)
          : resolveDefaultPositions(awayPlayers, "away");
      const defaults = new Map([...homeMap, ...awayMap]);

      if (restored && restored.size > 0) {
        // Merge: use restored positions for players still on field,
        // use defaults for new players (substitutions since last save)
        const merged = new Map<string, TacticalPosition>();
        for (const [pid, defPos] of defaults) {
          const saved = restored.get(pid);
          merged.set(pid, saved ?? defPos);
        }
        setPositions(merged);
      } else {
        // Only apply defaults if we don't have positions yet
        setPositions((prev) =>
          prev.size > 0 && !isNewMatch ? prev : defaults,
        );
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [matchId, homePlayers.length, awayPlayers.length]);

  /** Move a player to a new position, clamped to bounds and collision-resolved. */
  const movePlayer = useCallback(
    (
      playerId: string,
      newPos: TacticalPosition,
      playerPosition: string | undefined,
      side: "home" | "away",
    ) => {
      const bounds = getBoundsForPlayer(playerPosition, side, isMatchLive);
      const clamped = clampToBounds(newPos, bounds);
      setPositions((prev) => {
        // PMA-COCK-152: collision-avoidance must see the SAME positions
        // `getDisplayPosition` renders — including any player currently
        // resolved only via the `syncDefaultPositions` fallback (the async
        // IDB-load race window PMA-COCK-150 already documented: `prev`
        // starts as an empty Map and stays that way until the init effect's
        // `setPositions` lands). Checking only `prev` let a drag land
        // exactly on a teammate's visibly-rendered-but-not-yet-"real" slot
        // with zero avoidance — a genuine merge, live-proved via a unit
        // test that drags during the race window before this fix. `prev`
        // entries win over the synced default for any player who already
        // has a real (possibly IDB-restored or previously dragged) position.
        const effective = new Map(syncDefaultPositions);
        for (const [pid, pos] of prev) effective.set(pid, pos);
        const resolved = resolveCollision(playerId, clamped, effective, bounds);
        const next = new Map(prev);
        next.set(playerId, resolved);
        persistPositions(next);
        return next;
      });
    },
    [persistPositions, isMatchLive, syncDefaultPositions],
  );

  /** Apply a formation to one side — recalculates all positions for that side
   *  and persists both the formation and the new positions to IDB. Pass `null`
   *  to clear the formation and revert to default positions. */
  const applyFormation = useCallback(
    (side: "home" | "away", formation: Formation | null) => {
      if (!matchId) return;

      const players =
        side === "home" ? homePlayersRef.current : awayPlayersRef.current;

      // Compute new positions for this side
      const sidePositions =
        formation && isValidFormation(formation)
          ? resolveFormationPositions(players, side, formation)
          : resolveDefaultPositions(players, side);

      // Update formation state
      if (side === "home") {
        setHomeFormation(formation);
      } else {
        setAwayFormation(formation);
      }

      // Merge into current positions — only replace the affected side
      setPositions((prev) => {
        const next = new Map(prev);
        // Remove all players from this side first
        for (const p of players) {
          next.delete(p.id);
        }
        // Add new positions
        for (const [pid, pos] of sidePositions) {
          next.set(pid, pos);
        }
        persistPositions(next);
        return next;
      });

      // Persist formation state
      const newFormationState: FormationState = {
        home: side === "home" ? formation : homeFormation,
        away: side === "away" ? formation : awayFormation,
      };
      saveFormationsToIDB(matchId, newFormationState);
    },
    [matchId, persistPositions, homeFormation, awayFormation],
  );

  /** When a substitution happens, the incoming player inherits the outgoing
   *  player's position on the tactical field.
   *
   *  PMA-COCK-153: unlike `movePlayer`, this used to seat the incoming
   *  player on that inherited coordinate completely unchecked — a genuine
   *  gap, not a stylistic inconsistency. In real cockpit use an operator
   *  routinely repositions several players into a custom formation and
   *  THEN makes a substitution; if that reshuffle already left the outgoing
   *  player's slot crowded (a realistic outcome — see `resolveCollision`'s
   *  own iteration-budget comment above), the incoming substitute inherited
   *  that same crowding with zero attempt to separate. Once two tokens'
   *  rendered hit-test regions overlap this closely, hover/tap on one
   *  player's own visible label can silently resolve to a DIFFERENT
   *  player's node (live-confirmed via `document.elementFromPoint` against
   *  a running cockpit) — a data-integrity risk for an operator logging
   *  events by tapping the pitch. Full-field bounds are used here
   *  (not `getBoundsForPlayer`, which needs a position code/side this
   *  function doesn't receive) since the inherited coordinate already
   *  satisfied whatever positional bounds applied when it was set — this
   *  push is only ever a small nudge away from a collision, not a
   *  re-derivation of where the player is allowed to stand. */
  const applySubstitution = useCallback(
    (playerOffId: string, playerOnId: string) => {
      setPositions((prev) => {
        const pos = prev.get(playerOffId);
        if (!pos) return prev;
        const next = new Map(prev);
        next.delete(playerOffId);
        // PMA-COCK-153 (lead correction, 2026-08-25): `syncDefaultPositions`
        // holds a grid default for EVERY roster player, including the one
        // going off — `next.delete(playerOffId)` above only removes them
        // from `next`, so the overwrite loop below never touches that key,
        // and `effective` kept the outgoing player's PHANTOM grid-default
        // position. The incoming substitute was collision-checked against a
        // player who had just left the pitch; when that phantom sat near
        // the inherited coordinate, the substitute got shoved off the very
        // slot it's supposed to inherit (live-reproduced: 19.5 vs expected
        // 14 in `logger-field-flow.spec.ts`'s substitution test, same mode
        // in the double-substitution test). This was introduced BY the
        // earlier PMA-COCK-152b fix — collision correctly runs against
        // `syncDefaultPositions` merged with `positions`, but a
        // substitution is the one operation where a player is
        // INTENTIONALLY leaving the surface, so the merged view must drop
        // them explicitly too, not just from `next`.
        const effective = new Map(syncDefaultPositions);
        effective.delete(playerOffId);
        for (const [pid, p] of next) effective.set(pid, p);
        const resolved = resolveCollision(playerOnId, { ...pos }, effective, {
          xMin: 0,
          xMax: 100,
          yMin: 0,
          yMax: 100,
        });
        next.set(playerOnId, resolved);
        persistPositions(next);
        return next;
      });
    },
    [persistPositions, syncDefaultPositions],
  );

  /** Get the display position, flipped if needed. */
  const getDisplayPosition = useCallback(
    (playerId: string, flipSides: boolean): TacticalPosition => {
      // PMA-COCK-150: prefer the synchronously-computed grid default over a
      // literal centre point while the real position is still loading — see
      // syncDefaultPositions' own comment above. `{x:50,y:50}` stays as the
      // final fallback only for a genuinely unknown id (not in this side's
      // roster at all), which the grid computation itself would never cover.
      const canonical = positions.get(playerId) ??
        syncDefaultPositions.get(playerId) ?? { x: 50, y: 50 };
      // EPIC-97: "Voltear cancha" is a 180-degree rotation of the real pitch,
      // not a left-right mirror — both axes invert. See
      // `resolveCanonicalStatsbombLocation`'s matching comment in
      // TacticalField.tsx for the un-flip half of this relationship.
      if (flipSides) return { x: 100 - canonical.x, y: 100 - canonical.y };
      return canonical;
    },
    [positions, syncDefaultPositions],
  );

  /** Convert a display‑space drag delta back to canonical position. */
  const movePlayerFromDisplay = useCallback(
    (
      playerId: string,
      displayPos: TacticalPosition,
      flipSides: boolean,
      playerPosition: string | undefined,
      side: "home" | "away",
    ) => {
      // EPIC-97: inverse of getDisplayPosition's 180-degree rotation — both
      // axes invert, matching the display transform above.
      const canonical = flipSides
        ? { x: 100 - displayPos.x, y: 100 - displayPos.y }
        : displayPos;
      movePlayer(playerId, canonical, playerPosition, side);
    },
    [movePlayer],
  );

  /** Ensure a newly-entered player (substitution) gets placed. */
  const ensurePlayer = useCallback(
    (playerId: string, fallbackPos: TacticalPosition) => {
      setPositions((prev) => {
        if (prev.has(playerId)) return prev;
        const next = new Map(prev);
        next.set(playerId, fallbackPos);
        persistPositions(next);
        return next;
      });
    },
    [persistPositions],
  );

  /** Currently-dragged player id (exposed so TacticalField can show bounds). */
  const [draggingPlayerId, setDraggingPlayerId] = useState<string | null>(null);

  return {
    positions,
    movePlayer,
    movePlayerFromDisplay,
    applySubstitution,
    getDisplayPosition,
    ensurePlayer,
    draggingPlayerId,
    setDraggingPlayerId,
    homeFormation,
    awayFormation,
    applyFormation,
  };
};
